import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { onSnapshot, updateDoc, getDoc, addDoc, getDocs, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { useToast } from '../../contexts/ToastContext';
import { downloadBlockedReason } from '../../features/finalGate/downloadCheck';
import DownloadButton from '../../features/downloads/DownloadButton';
import { useDocumentDownload } from '../../features/downloads/useDocumentDownload';
import { consentDownloadRequest, selectConsentArtifact } from '../../features/downloads/consentDownload';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { auth } from '../../firebase';
import { consentTabState } from '../../utils/consentTabState';
import {
  projectDoc,
  protocolAnalysisLatestDoc,
  studyDesignConfigDoc,
  artifactsCollection,
  artifactDoc,
  artifactVersionsCollection,
} from '../../constants/firestorePaths';
import { readDocVersion, docChangedMessage } from '../../utils/versionedDoc';
import { versionedDocFor } from '../../utils/versionedFirestoreDoc';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { usePermission } from '../../components/permissions/PermissionTooltip';
import {
  FlaskConical,
  Users,
  Shuffle,
  Plus,
  Trash2,
  Save,
  Loader2,
  AlertCircle,
  ShieldCheck,
  Edit2,
  FileText,
  RotateCcw,
  Clock,
} from 'lucide-react';
import ArtifactBuilder from '../../components/ArtifactBuilder';
import { forDisplay, NEW_STUDY_RANDOMIZATION } from '../../features/studyDesign/storedRandomization';

interface StudyArm {
  id: string;
  name: string;
  description: string;
  ratio: number;
  treatment: string;
}

interface Visit {
  id: string;
  name: string;
  /** null = the protocol states no study day for this visit. */
  day: number | null;
  windowBefore: number;
  windowAfter: number;
  required: boolean;
  forms: string[];
}

interface StudyDesignData {
  arms: StudyArm[];
  visits: Visit[];
  randomization: {
    enabled: boolean;
    method: string;
    stratificationFactors: string[];
  };
  subjectIdFormat: string;
}

const defaultStudyDesign: StudyDesignData = {
  arms: [
    { id: 'arm-1', name: 'Treatment', description: 'Active treatment arm', ratio: 1, treatment: 'Active Drug' },
    { id: 'arm-2', name: 'Placebo', description: 'Placebo control arm', ratio: 1, treatment: 'Placebo' }
  ],
  visits: [
    { id: 'v-scr', name: 'Screening', day: -14, windowBefore: 0, windowAfter: 7, required: true, forms: ['demographics', 'inclusion_exclusion', 'medical_history'] },
    { id: 'v-bl', name: 'Baseline', day: 0, windowBefore: 0, windowAfter: 0, required: true, forms: ['vitals', 'physical_exam', 'labs'] },
    { id: 'v-1', name: 'Week 4', day: 28, windowBefore: 3, windowAfter: 3, required: true, forms: ['vitals', 'adverse_events'] },
    { id: 'v-2', name: 'Week 8', day: 56, windowBefore: 3, windowAfter: 3, required: true, forms: ['vitals', 'labs', 'adverse_events'] },
    { id: 'v-eos', name: 'End of Study', day: 84, windowBefore: 3, windowAfter: 7, required: true, forms: ['vitals', 'physical_exam', 'labs', 'adverse_events', 'disposition'] }
  ],
  // Nothing stated until the protocol or a person states it (storedRandomization.ts).
  randomization: NEW_STUDY_RANDOMIZATION,
  subjectIdFormat: ''
};

export default function StudyDesign() {
  const { projectId } = useParams<{ projectId: string }>();
  const canEdit = usePermission('edit');
  const [studyDesign, setStudyDesign] = useState<StudyDesignData>(defaultStudyDesign);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Consent form state
  const [consentArtifact, setConsentArtifact] = useState<any>(null);
  const [consentMode, setConsentMode] = useState<'view' | 'builder'>('view');
  const [projectName, setProjectName] = useState('');
  const [consentVersions, setConsentVersions] = useState<any[]>([]);
  const [savingVersion, setSavingVersion] = useState(false);
  const [activeTab, setActiveTab] = useState<'arms' | 'randomization' | 'settings' | 'consent'>('arms');
  const [hasChanges, setHasChanges] = useState(false);
  const [_protocolAnalysis, setProtocolAnalysis] = useState<any>(null);
  // True when a successful protocol analysis is stored (not a failure marker).
  const [hasAnalysis, setHasAnalysis] = useState(false);
  const [analysisKnown, setAnalysisKnown] = useState(false);
  const [artifactsKnown, setArtifactsKnown] = useState(false);
  const [creatingConsent, setCreatingConsent] = useState(false);
  const [consentCreateError, setConsentCreateError] = useState<string | null>(null);

  const { showToast } = useToast();
  // The same consent download as the Study Documents card (features/downloads).
  const consentDownload = useDocumentDownload(projectId, 'consentForm');
  const consentPrecheck = () => downloadBlockedReason(projectId, 'consent_form');
  // Optimistic concurrency: the version of studyDesign/config this page holds.
  // The Visits page (VisitManager.tsx) saves the same document.
  const designVersion = useMemo(
    () => (projectId ? versionedDocFor(studyDesignConfigDoc(projectId)) : null),
    [projectId],
  );

  useEffect(() => {
    if (!projectId) return;

    // Load protocol analysis for auto-population
    const loadAnalysis = async () => {
      try {
        const analysisDoc = await getDoc(protocolAnalysisLatestDoc(projectId));
        if (analysisDoc.exists()) {
          setProtocolAnalysis(analysisDoc.data().facts || analysisDoc.data());
        }
      } catch (error) {
        console.error('Error loading protocol analysis:', error);
      }
    };

    // Live: an analysis finishing while this page is open must update the tab.
    const analysisUnsub = onSnapshot(
      protocolAnalysisLatestDoc(projectId),
      (snap) => {
        setHasAnalysis(snap.exists() && snap.data()?.status !== 'extraction_failed');
        setAnalysisKnown(true);
      },
      () => setAnalysisKnown(true),
    );

    // Load study design
    const unsubscribe = onSnapshot(
      studyDesignConfigDoc(projectId),
      (snapshot) => {
        if (snapshot.exists()) {
          // This page always takes on the stored copy, so it records its version.
          designVersion?.observe(readDocVersion(snapshot.data()));
          setStudyDesign(forDisplay(snapshot.data() as StudyDesignData));
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error loading study design:', error);
        setLoading(false);
      }
    );

    loadAnalysis();

    // Load project name
    getDoc(projectDoc(projectId)).then(snap => {
      if (snap.exists()) setProjectName(snap.data().name || '');
    });

    // Listen for consent form artifact
    const consentUnsub = onSnapshot(
      artifactsCollection(projectId),
      (snapshot) => {
        setArtifactsKnown(true);
        // The same rule as the Study Documents card (selectConsentArtifact):
        // the last consent_form, so both pages download the same consent.
        const artifact = selectConsentArtifact(
          snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as { id: string; type?: unknown; [k: string]: any })),
        );
        if (artifact) {
          setConsentArtifact(artifact);
          // Load versions for this artifact
          getDocs(query(
            artifactVersionsCollection(projectId, artifact.id),
            orderBy('savedAt', 'desc')
          )).then(vSnap => {
            setConsentVersions(vSnap.docs.map(v => ({ id: v.id, ...v.data() })));
          }).catch(() => {});
        }
      },
      () => setArtifactsKnown(true),
    );

    return () => { unsubscribe(); consentUnsub(); analysisUnsub(); };
  }, [projectId, designVersion]);

  // Recovery for a study that was analysed but has no consent form record.
  const handleCreateConsent = async () => {
    if (!projectId) return;
    setCreatingConsent(true);
    setConsentCreateError(null);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const response = await fetch(
        `${import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || ''}/generateAllFromProtocolV2`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
          body: JSON.stringify({ projectId }),
        },
      );
      if (!response.ok) throw new Error(`Request failed (${response.status})`);
      // The artifacts listener above picks the new record up and shows the builder.
    } catch (error) {
      console.error('Error creating consent form:', error);
      setConsentCreateError('The consent form could not be created. Please try again in a moment.');
    } finally {
      setCreatingConsent(false);
    }
  };

  const handleSave = async () => {
    if (!projectId || !designVersion) return;
    setSaving(true);
    try {
      // The whole document, from this page's copy: claims the version the copy
      // came from plus one, and the rules refuse it if the stored study design
      // has moved since (another tab, the Visits page, a protocol re-analysis,
      // a Schedule of Events import).
      const outcome = await designVersion.write(async (next) => {
        const data = { ...studyDesign, docVersion: next } as any;
        try {
          await updateDoc(studyDesignConfigDoc(projectId), data);
        } catch (error: any) {
          // If doc doesn't exist, create it
          if (error?.code !== 'not-found') throw error;
          const { setDoc } = await import('firebase/firestore');
          await setDoc(studyDesignConfigDoc(projectId), data);
        }
      });
      if (outcome === 'saved') {
        setHasChanges(false);
      } else {
        showToast(docChangedMessage('The study design'), 'error');
      }
    } catch (error: any) {
      console.error('Error saving study design:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateStudyDesign = (updates: Partial<StudyDesignData>) => {
    setStudyDesign(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const addArm = () => {
    const newArm: StudyArm = {
      id: `arm-${Date.now()}`,
      name: `Arm ${studyDesign.arms.length + 1}`,
      description: '',
      ratio: 1,
      treatment: ''
    };
    updateStudyDesign({ arms: [...studyDesign.arms, newArm] });
  };

  const removeArm = (id: string) => {
    updateStudyDesign({ arms: studyDesign.arms.filter(arm => arm.id !== id) });
  };

  const updateArm = (id: string, updates: Partial<StudyArm>) => {
    updateStudyDesign({
      arms: studyDesign.arms.map(arm => arm.id === id ? { ...arm, ...updates } : arm)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Save current consent content as a version, then perform an action. A
  // redraft can also start from the builder before the form is finished
  // (the study countries changed), so a form with no finished text still
  // saves its section drafts as a version first.
  const saveConsentVersion = async (andThen: 'edit' | 'redraft') => {
    if (!projectId || !consentArtifact) return;
    if (andThen === 'edit' && !consentArtifact.content) return;
    setSavingVersion(true);
    try {
      const versionNum = (consentArtifact.version || 1);
      // Save snapshot to versions subcollection
      await addDoc(
        artifactVersionsCollection(projectId, consentArtifact.id),
        {
          version: versionNum,
          content: consentArtifact.content || '',
          status: consentArtifact.status,
          builderState: consentArtifact.builderState || null,
          savedAt: serverTimestamp(),
        }
      );

      if (andThen === 'redraft') {
        // Reset the builder state and increment version
        const { httpsCallable } = await import('../../lib/callable');
        const { functions } = await import('../../firebase');
        const initFn = httpsCallable(functions, CLOUD_FUNCTIONS.INITIALIZE_CONSENT_BUILDER, { timeout: CALLABLE_TIMEOUTS.SHORT });
        await initFn({ projectId, artifactId: consentArtifact.id });
        // Increment version on the artifact
        await updateDoc(artifactDoc(projectId, consentArtifact.id), {
          version: versionNum + 1,
          status: 'draft',
          content: '',
        });
      }

      // Reload versions
      const vSnap = await getDocs(query(
        artifactVersionsCollection(projectId, consentArtifact.id),
        orderBy('savedAt', 'desc')
      ));
      setConsentVersions(vSnap.docs.map(v => ({ id: v.id, ...v.data() })));

      setConsentMode('builder');
    } catch (err) {
      console.error('Error saving consent version:', err);
    } finally {
      setSavingVersion(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Study Design</h1>
          {/*
            CORRECTED 2026-09-05 (Jim). This read "Configure study arms, visits,
            and randomization" and had done since f907680d moved visit editing
            into the CRF Builder in June. The page's tabs are Study Arms,
            Randomization, Consent Form and Settings — there has been no visit
            editing here for three months. Jim came looking on the strength of
            this sentence and concluded the feature had been deleted.

            A heading that names something the page does not do is worse than
            saying nothing: it sends people to the wrong place and then makes
            them doubt the feature exists. The pointer below is the other half —
            saying where it went, not merely removing the claim.
          */}
          <p className="text-gray-600">Configure study arms, randomization, consent and study settings</p>
          <p className="text-sm text-gray-500 mt-1">
            Visits and the Schedule of Events are managed in the{' '}
            <Link
              to={`/project/${projectId}/crf-builder`}
              className="text-primary-700 font-medium underline underline-offset-2 hover:text-primary-800"
            >
              CRF Builder
            </Link>
            , under the <span className="font-medium">Visits</span> view.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {hasChanges && (
            <span className="text-sm text-amber-600 flex items-center">
              <AlertCircle className="h-4 w-4 mr-1" />
              Unsaved changes
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges || !canEdit}
            title={!canEdit ? "Your role doesn't allow editing on this project." : undefined}
            className="px-4 py-2 btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>Save</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'arms', name: 'Study Arms', icon: <FlaskConical className="h-4 w-4" /> },
            { id: 'randomization', name: 'Randomization', icon: <Shuffle className="h-4 w-4" /> },
            { id: 'consent', name: 'Consent Form', icon: <ShieldCheck className="h-4 w-4" /> },
            { id: 'settings', name: 'Settings', icon: <Users className="h-4 w-4" /> }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="card p-6">
        {/* Study Arms Tab */}
        {activeTab === 'arms' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Treatment Arms</h2>
              <button
                onClick={addArm}
                disabled={!canEdit}
                title={!canEdit ? "Your role doesn't allow editing on this project." : undefined}
                className="px-3 py-2 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add Arm</span>
              </button>
            </div>

            <div className="space-y-4">
              {studyDesign.arms.map((arm, index) => (
                <div key={arm.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <span className="h-8 w-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-medium text-sm">
                        {index + 1}
                      </span>
                      <input
                        type="text"
                        value={arm.name}
                        onChange={(e) => updateArm(arm.id, { name: e.target.value })}
                        className="text-lg font-semibold text-gray-900 bg-transparent border-none focus:outline-none focus:ring-0"
                      />
                    </div>
                    <button
                      onClick={() => removeArm(arm.id)}
                      className="p-2 text-gray-400 hover:text-red-500 rounded"
                      disabled={studyDesign.arms.length <= 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Treatment</label>
                      <input
                        type="text"
                        value={arm.treatment}
                        onChange={(e) => updateArm(arm.id, { treatment: e.target.value })}
                        placeholder="e.g., Drug A 100mg"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Randomization Ratio</label>
                      <input
                        type="number"
                        value={arm.ratio}
                        onChange={(e) => updateArm(arm.id, { ratio: parseInt(e.target.value) || 1 })}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <input
                        type="text"
                        value={arm.description}
                        onChange={(e) => updateArm(arm.id, { description: e.target.value })}
                        placeholder="Brief description"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                <strong>Randomization Ratio:</strong>{' '}
                {studyDesign.arms.map(a => a.ratio).join(':')}
                {' '}({studyDesign.arms.map(a => a.name).join(' : ')})
              </p>
            </div>
          </div>
        )}

        {/* Randomization Tab */}
        {activeTab === 'randomization' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Randomization Settings</h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Enable Randomization</p>
                  <p className="text-sm text-gray-600">Randomize subjects to treatment arms</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={studyDesign.randomization.enabled}
                    onChange={(e) => updateStudyDesign({
                      randomization: { ...studyDesign.randomization, enabled: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              {studyDesign.randomization.enabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Randomization Method</label>
                    <select
                      value={studyDesign.randomization.method}
                      onChange={(e) => updateStudyDesign({
                        randomization: { ...studyDesign.randomization, method: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Not stated</option>
                      <option value="Block Randomization">Block Randomization</option>
                      <option value="Simple Randomization">Simple Randomization</option>
                      <option value="Stratified Randomization">Stratified Randomization</option>
                      <option value="Minimization">Minimization</option>
                      <option value="Adaptive Randomization">Adaptive Randomization</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Stratification Factors</label>
                    <div className="space-y-2">
                      {studyDesign.randomization.stratificationFactors.map((factor, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={factor}
                            onChange={(e) => {
                              const newFactors = [...studyDesign.randomization.stratificationFactors];
                              newFactors[index] = e.target.value;
                              updateStudyDesign({
                                randomization: { ...studyDesign.randomization, stratificationFactors: newFactors }
                              });
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                          <button
                            onClick={() => {
                              const newFactors = studyDesign.randomization.stratificationFactors.filter((_, i) => i !== index);
                              updateStudyDesign({
                                randomization: { ...studyDesign.randomization, stratificationFactors: newFactors }
                              });
                            }}
                            className="p-2 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          updateStudyDesign({
                            randomization: {
                              ...studyDesign.randomization,
                              stratificationFactors: [...studyDesign.randomization.stratificationFactors, '']
                            }
                          });
                        }}
                        className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Add Factor</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Subject ID Settings</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject ID Format</label>
              <input
                type="text"
                value={studyDesign.subjectIdFormat}
                onChange={(e) => updateStudyDesign({ subjectIdFormat: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="mt-2 text-sm text-gray-500">
                Available tokens: {'{SITE}'}, {'{SEQUENCE:n}'} (n = digits), {'{YEAR}'}, {'{MONTH}'}
              </p>
              <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Preview:</strong> {studyDesign.subjectIdFormat
                    .replace('{SITE}', '001')
                    .replace('{SEQUENCE:4}', '0001')
                    .replace('{SEQUENCE:3}', '001')
                    .replace('{YEAR}', '2024')
                    .replace('{MONTH}', '01')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Consent Form Tab */}
        {activeTab === 'consent' && (
          <div>
            {consentMode === 'builder' && consentArtifact ? (
              <ArtifactBuilder
                artifactId={consentArtifact.id}
                projectId={projectId!}
                onClose={() => setConsentMode('view')}
                onRedraft={() => saveConsentVersion('redraft')}
              />
            ) : consentArtifact?.content ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      consentArtifact.status === 'generated' || consentArtifact.status === 'approved'
                        ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {consentArtifact.status === 'generated' || consentArtifact.status === 'approved' ? 'Complete' : 'Draft'}
                    </span>
                    {projectName && <span className="text-sm text-gray-400">{projectName}</span>}
                  </div>
                  <div className="flex gap-2">
                    <DownloadButton
                      download={consentDownload}
                      options={[{
                        label: 'Word',
                        title: 'Download the Informed Consent Form as a Word document',
                        request: consentDownloadRequest({
                          content: consentArtifact.content,
                          projectName,
                          precheck: consentPrecheck,
                        }),
                      }]}
                    />
                    <button
                      onClick={() => saveConsentVersion('edit')}
                      disabled={savingVersion}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                    >
                      <Edit2 className="h-4 w-4" />
                      Edit Sections
                    </button>
                    <button
                      onClick={() => saveConsentVersion('redraft')}
                      disabled={savingVersion}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                    >
                      {savingVersion ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                      Redraft
                    </button>
                  </div>
                </div>

                {/* Document preview */}
                <div className="bg-gray-50 rounded-lg p-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 24rem)' }}>
                  <div className="max-w-3xl mx-auto prose prose-sm prose-gray">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {consentArtifact.content}
                    </ReactMarkdown>
                  </div>
                </div>

                {/* Version history */}
                {consentVersions.length > 0 && (
                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-700">Version History</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between px-3 py-2 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-green-800">v{consentArtifact.version || 1}</span>
                          <span className="text-xs text-green-600">Current</span>
                        </div>
                      </div>
                      {consentVersions.map((v) => (
                        <div key={v.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-700">v{v.version}</span>
                            <span className="text-xs text-gray-500">
                              {v.savedAt?.toDate ? v.savedAt.toDate().toLocaleDateString() : ''}
                            </span>
                          </div>
                          {v.content && (
                            <DownloadButton
                              download={consentDownload}
                              variant="secondary"
                              size="sm"
                              options={[{
                                label: 'Word',
                                title: `Download version ${v.version} of the Informed Consent Form as a Word document`,
                                request: consentDownloadRequest({
                                  content: v.content,
                                  projectName,
                                  version: v.version,
                                  precheck: consentPrecheck,
                                }),
                              }]}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : consentArtifact ? (
              <div className="text-center py-12">
                <FileText className="h-10 w-10 mx-auto mb-3 text-primary-600" />
                <h3 className="text-lg font-medium text-gray-800 mb-2">Consent Form In Progress</h3>
                <p className="text-sm text-gray-500 mb-4">Open the builder to generate and approve sections.</p>
                <button
                  onClick={() => setConsentMode('builder')}
                  className="px-5 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
                >
                  Open Builder
                </button>
              </div>
            ) : consentTabState(hasAnalysis, !!consentArtifact, analysisKnown && artifactsKnown) === 'loading' ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : consentTabState(hasAnalysis, !!consentArtifact, analysisKnown && artifactsKnown) === 'create_button' ? (
              <div className="text-center py-12">
                <FileText className="h-10 w-10 mx-auto mb-3 text-primary-600" />
                <h3 className="text-lg font-medium text-gray-800 mb-2">No Consent Form Yet</h3>
                <p className="text-sm text-gray-500 mb-4">This study's protocol has been analyzed, but its consent form has not been created.</p>
                <button
                  onClick={handleCreateConsent}
                  disabled={creatingConsent}
                  className="px-5 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  {creatingConsent ? 'Creating...' : 'Create consent form'}
                </button>
                {consentCreateError && (
                  <p className="text-sm text-red-600 mt-3" role="alert">{consentCreateError}</p>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <AlertCircle className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-800 mb-2">No Consent Form</h3>
                <p className="text-sm text-gray-500">A consent form is generated when you upload and analyze a protocol.</p>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}
